import React from "react";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";

export interface TimeSlot {
  id: string;
  time: string; // e.g., "9:00 AM"
  duration: number; // in minutes
  isAvailable: boolean;
}

interface TimeSlotSelectorProps {
  availableSlots: TimeSlot[];
  selectedSlot: string | null;
  onSelectSlot: (slotId: string) => void;
  className?: string;
}

export function TimeSlotSelector({
  availableSlots,
  selectedSlot,
  onSelectSlot,
  className,
}: TimeSlotSelectorProps) {
  // Group time slots by morning and afternoon
  const morningSlots = availableSlots.filter(
    (slot) => slot.time.includes("AM") && slot.isAvailable
  );
  const afternoonSlots = availableSlots.filter(
    (slot) => slot.time.includes("PM") && slot.isAvailable
  );

  // Function to render a group of time slots
  const renderTimeSlots = (slots: TimeSlot[], label: string) => {
    if (slots.length === 0) return null;

    return (
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">{label}</h4>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {slots.map((slot) => (
            <Button
              key={slot.id}
              variant="outline"
              size="sm"
              className={cn(
                "h-auto py-2 px-3 flex flex-col items-center justify-center",
                selectedSlot === slot.id
                  ? "bg-blue-50 border-blue-500 text-blue-700"
                  : "hover:bg-blue-50 hover:border-blue-300"
              )}
              onClick={() => onSelectSlot(slot.id)}
              disabled={!slot.isAvailable}
            >
              <Clock className="w-3 h-3 mb-1" />
              <span className="text-xs">{slot.time}</span>
              <span className="text-[10px] text-gray-500">
                {slot.duration} min
              </span>
            </Button>
          ))}
        </div>
      </div>
    );
  };

  // If no slots are available
  if (availableSlots.length === 0 || availableSlots.every(slot => !slot.isAvailable)) {
    return (
      <div className={cn("text-center py-6", className)}>
        <p className="text-gray-500">No available time slots for this date.</p>
        <p className="text-sm text-gray-400 mt-1">Please select another date.</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {renderTimeSlots(morningSlots, "Morning")}
      {renderTimeSlots(afternoonSlots, "Afternoon")}
    </div>
  );
}
